import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, Trash2, Loader2, ShieldCheck, Undo2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const errMsg = (e) => formatApiError(e?.response?.data?.detail) || e.message;

function DeleteDialog({ open, onClose }) {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const [step, setStep] = useState("warn");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const close = () => onClose();
  useEffect(() => { if (open) { setStep("warn"); setCode(""); } }, [open]);
  const send = async () => {
    setBusy(true);
    try { await api.post("/privacy/delete/request"); setStep("code"); toast.success(t("privacy.codeSent")); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try { await api.post("/privacy/delete/confirm", { code }); await refresh(); toast.success(t("privacy.scheduledToast")); close(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="privacy-delete-dialog">
        <DialogHeader>
          <DialogTitle>{t("privacy.deleteTitle")}</DialogTitle>
          <DialogDescription>{step === "warn" ? t("privacy.deleteWarn") : t("privacy.enterCode")}</DialogDescription>
        </DialogHeader>
        {step === "warn" ? (
          <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
            <li>{t("privacy.bulletGrace")}</li><li>{t("privacy.bulletGone")}</li><li>{t("privacy.bulletRecords")}</li>
          </ul>
        ) : (
          <Input data-testid="privacy-delete-code-input" inputMode="numeric" maxLength={6} value={code} autoFocus
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="font-mono text-lg tracking-[0.4em] text-center h-12" />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="privacy-delete-cancel-btn">{t("privacy.keepAccount")}</Button>
          {step === "warn" ? (
            <Button onClick={send} disabled={busy} data-testid="privacy-delete-send-code-btn" className="bg-red-600 hover:bg-red-500 text-white">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{t("privacy.sendCode")}
            </Button>
          ) : (
            <Button onClick={confirm} disabled={busy || code.length !== 6} data-testid="privacy-delete-confirm-btn" className="bg-red-600 hover:bg-red-500 text-white">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{t("privacy.confirmDelete")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduledBox({ date }) {
  const { t, i18n } = useTranslation();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const label = new Date(date).toLocaleDateString(i18n.language || "en", { month: "long", day: "numeric", year: "numeric" });
  const cancel = async () => {
    setBusy(true);
    try { await api.post("/privacy/delete/cancel"); await refresh(); toast.success(t("privacy.cancelledToast")); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="privacy-deletion-scheduled" className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4">
      <div className="text-sm font-semibold text-red-900">{t("privacy.scheduledTitle", { date: label })}</div>
      <p className="text-xs text-red-800 mt-1">{t("privacy.scheduledBody")}</p>
      <Button size="sm" onClick={cancel} disabled={busy} data-testid="privacy-cancel-deletion-btn" className="mt-3 bg-slate-900 hover:bg-slate-800 text-white h-9">
        {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Undo2 size={13} className="mr-1.5" />}{t("privacy.cancelDeletion")}
      </Button>
    </div>
  );
}

export default function PrivacyDataCard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/privacy/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `peak-market-society-data-${new Date().toISOString().slice(0, 10)}.json` });
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("privacy.downloaded"));
    } catch (e) { toast.error(errMsg(e)); } finally { setDownloading(false); }
  };
  return (
    <div data-testid="account-privacy-card" className="bg-white border border-slate-200 rounded-2xl p-8">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0"><ShieldCheck size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">{t("privacy.title")}</div>
          <p className="mt-2 text-sm text-slate-600 max-w-lg">{t("privacy.body")}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button variant="outline" onClick={download} disabled={downloading} data-testid="privacy-download-btn" className="h-10 border-slate-300">
              {downloading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}{t("privacy.download")}
            </Button>
            {!user?.deletion_scheduled_for && user?.role !== "admin" && (
              <Button variant="outline" onClick={() => setOpen(true)} data-testid="privacy-delete-btn" className="h-10 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800">
                <Trash2 size={14} className="mr-2" />{t("privacy.delete")}
              </Button>
            )}
          </div>
          {user?.deletion_scheduled_for && <ScheduledBox date={user.deletion_scheduled_for} />}
        </div>
      </div>
      <DeleteDialog open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
